import { CategoryHeader } from './CategoryHeader';
import { CountyRow, type AmenityCategoryKey } from './CountyRow';
import { cn } from '@/lib/utils';
import type {
  AdminAmenitiesDTO,
  AdminMatrixCell,
  AdminRefreshScope,
} from './types';

/**
 * `<AmenityMatrix>` — 14×4 county × category table (ADMIN-01). Composes
 * <CountyRow> + <CategoryHeader>. Emits a single `onRefresh(scope)`
 * callback; the parent <AdminPage> wires it to useTriggerRefresh.mutate.
 *
 * Per-cell and per-row buttons map to `{ scope: 'county', county }` since
 * the API surface (POST /api/admin/refresh) supports only county / category /
 * all scopes — see web/routes/api.js. The per-cell button is a convenience
 * affordance for a stale-looking row; the per-row button is the explicit
 * control. Both invoke the same scope.
 *
 * pendingMap keys (parent supplies):
 *   `county:{slug}`    — bool, refresh-in-flight for this county
 *   `category:{cat}`   — bool, refresh-in-flight for this category
 *   `all`              — bool (unused at this level — page-level CTA)
 */
const CATEGORY_ORDER: AmenityCategoryKey[] = ['dog_park', 'beach', 'daycare', 'bar'];
const CATEGORY_HEADERS: Record<
  AmenityCategoryKey,
  { display: string; pluralLower: string }
> = {
  dog_park: { display: 'Dog Parks', pluralLower: 'dog parks' },
  beach: { display: 'Beaches', pluralLower: 'beaches' },
  daycare: { display: 'Daycare', pluralLower: 'daycare' },
  bar: { display: 'Bars', pluralLower: 'dog-friendly bars' },
};

function emptyCell(): AdminMatrixCell {
  return { count: 0, status: null, last_checked_at: null, gap_reason: null };
}

export interface AmenityMatrixProps {
  data: AdminAmenitiesDTO;
  pendingMap: Record<string, boolean>;
  onRefresh: (scope: AdminRefreshScope) => void;
}

export function AmenityMatrix({ data, pendingMap, onRefresh }: AmenityMatrixProps) {
  return (
    <table
      role="table"
      className={cn(
        'w-full border-collapse text-foam-50',
        'rounded-lg overflow-hidden',
      )}
    >
      <thead>
        <tr>
          <th
            scope="col"
            className="sticky top-16 z-30 p-3 text-left text-sm font-semibold text-foam-50 bg-ocean-900/95 border border-white/5"
          >
            County
          </th>
          {CATEGORY_ORDER.map((cat) => (
            <CategoryHeader
              key={cat}
              label={CATEGORY_HEADERS[cat].display}
              pluralLower={CATEGORY_HEADERS[cat].pluralLower}
              pending={pendingMap[`category:${cat}`] === true}
              onRefresh={() => onRefresh({ scope: 'category', category: cat })}
            />
          ))}
          <th
            scope="col"
            className="sticky top-16 z-30 p-3 text-left text-sm font-semibold text-foam-50 bg-ocean-900/95 border border-white/5"
          >
            Row
          </th>
        </tr>
      </thead>
      <tbody>
        {data.counties.map((county) => {
          const cells: Record<AmenityCategoryKey, AdminMatrixCell> = {
            dog_park: data.matrix[county]?.dog_park ?? emptyCell(),
            beach: data.matrix[county]?.beach ?? emptyCell(),
            daycare: data.matrix[county]?.daycare ?? emptyCell(),
            bar: data.matrix[county]?.bar ?? emptyCell(),
          };
          return (
            <CountyRow
              key={county}
              county={county}
              cells={cells}
              pendingMap={pendingMap}
              onRefreshCell={() => onRefresh({ scope: 'county', county })}
              onRefreshRow={() => onRefresh({ scope: 'county', county })}
            />
          );
        })}
      </tbody>
    </table>
  );
}
